import { useEffect, useState } from 'react';
import {
  BillingInterval, BillingPricing, getBillingPricing, startCheckout,
} from '../lib/billing';
import {
  EMPTY_FISCAL, FiscalData, FiscalDataFields, FiscalDataResponse,
  fiscalErrMsg, getFiscalData, saveCfdiOptin, saveFiscalData,
} from '../components/FiscalDataFields';
import { useAuth } from './AuthProvider';

interface Props {
  open: boolean;
  onClose: () => void;
}

const mxn = (cents: number) =>
  `$${(cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * 1a (§17.6) — selector de plan ANTES del Checkout hospedado: mensual vs anual
 * (anual = mensual × 11, un mes gratis), desglose de sucursales adicionales
 * (+30% c/u), descuento vigente (referido −10% / competencia −30%, lo decide el
 * backend) e IVA 16% por fuera. El cliente SOLO elige el intervalo; precios,
 * cupón y quantity se resuelven server-side (GET /billing/pricing).
 *
 * Hint aprobado: en anual con sucursales, agregar una sucursal a mitad de año
 * genera el cargo prorrateado del resto del año en la siguiente factura.
 *
 * V85 — opt-in de CFDI en el propio flujo: toggle "¿Quieres factura?" (default
 * SÍ — MX B2B; bajarlo a no-preseleccionado es cambiar UN valor) + los mismos
 * campos fiscales de Configuración (FiscalDataFields). Con Sí, los datos se
 * guardan (PUT /me/fiscal-data, validación server-side) ANTES de ir a Stripe →
 * el 1er CFDI sale al instante, sin needs_data. Con No, opt-out y checkout
 * directo, sin fricción — sus cobros quedan `opted_out` (cero avisos).
 */
export function SubscribeDialog({ open, onClose }: Props) {
  // V217 (§1ter): los datos fiscales los captura SOLO el titular. Otro admin puede
  // suscribirse; la factura queda esperando datos hasta que el titular los capture (D10).
  const { user } = useAuth() as any;
  const isOwner = !!user?.isOwner;
  const [pricing, setPricing] = useState<BillingPricing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BillingInterval | null>(null);
  const [wantsCfdi, setWantsCfdi] = useState(true); // default Sí (aprobado 2026-07-10)
  const [form, setForm] = useState<FiscalData>(EMPTY_FISCAL);
  const [catalogs, setCatalogs] = useState<FiscalDataResponse['catalogs'] | null>(null);
  const [hadData, setHadData] = useState(false);
  const [fiscalError, setFiscalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPricing(null);
    setError(null);
    setFiscalError(null);
    getBillingPricing()
      .then(setPricing)
      .catch((e) => setError(e?.message ?? 'No se pudo cargar el precio de tu plan.'));
    // Pre-llena los datos fiscales si ya existen (best-effort: el bloque
    // funciona con defaults si esto falla).
    if (!isOwner) return;
    getFiscalData()
      .then((r) => {
        setCatalogs(r.catalogs);
        setHadData(r.complete);
        if (r.wantsCfdi === false) setWantsCfdi(false);
        if (r.data) setForm({
          rfc: r.data.rfc, razonSocial: r.data.razonSocial, regimenFiscal: r.data.regimenFiscal,
          usoCfdi: r.data.usoCfdi, cp: r.data.cp,
        });
      })
      .catch(() => { /* defaults */ });
  }, [open, isOwner]);

  if (!open) return null;

  const subscribe = async (interval: BillingInterval) => {
    setBusy(interval);
    setFiscalError(null);
    try {
      if (!isOwner) {
        // Sin paso fiscal: lo captura el titular en Configuración → Datos fiscales.
      } else if (wantsCfdi) {
        // Valida y guarda ANTES de Stripe (server-side, mismo DTO de
        // Configuración). Error → se muestra inline y NO se abre el checkout.
        await saveFiscalData(form); // implica opt-in TRUE en el backend
      } else {
        await saveCfdiOptin(false); // NO explícito: checkout sin fricción
      }
    } catch (e: any) {
      setBusy(null);
      setFiscalError(fiscalErrMsg(e, 'Revisa tus datos fiscales.'));
      return;
    }
    startCheckout(interval).catch(() => {
      setBusy(null);
      alert('No se pudo iniciar el pago. Intenta de nuevo o contáctanos.');
    });
  };

  const card = (interval: BillingInterval, title: string, badge?: string) => {
    if (!pricing) return null;
    const p = pricing.intervals[interval];
    const per = interval === 'monthly' ? '/mes' : '/año';
    return (
      <div className="flex flex-1 flex-col rounded-xl border border-slate-200 bg-white p-4 text-left">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {badge && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              {badge}
            </span>
          )}
        </div>

        <div className="mt-3 space-y-1 text-xs text-slate-600">
          <div className="flex justify-between">
            <span>Plan base</span>
            <span>{mxn(p.baseAmount)}{per}</span>
          </div>
          {pricing.extraBranches > 0 && (
            <div className="flex justify-between">
              <span>{pricing.extraBranches} sucursal{pricing.extraBranches === 1 ? '' : 'es'} adicional{pricing.extraBranches === 1 ? '' : 'es'} (+30% c/u)</span>
              <span>{mxn(p.branchUnitAmount * pricing.extraBranches)}{per}</span>
            </div>
          )}
          {p.discountPercent > 0 && (
            <div className="flex justify-between text-emerald-700">
              <span>Descuento −{p.discountPercent}% ({p.couponDurationLabel})</span>
              <span>−{mxn(p.subtotal - p.discountedSubtotal)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>IVA 16%</span>
            <span>{mxn(p.iva)}</span>
          </div>
        </div>

        <div className="mt-3 border-t border-slate-100 pt-2">
          <p className="text-lg font-semibold text-slate-900">
            {mxn(p.total)} <span className="text-xs font-normal text-slate-500">MXN {per}</span>
          </p>
          {/* "Después del periodo con descuento" SOLO para descuentos que VENCEN.
              Se cuelga de `discountExpires` (lo decide el backend por la vigencia), NO del
              nombre del tipo (v14 §2.1): Fundador = de por vida → no se muestra; Adopción
              (vence al año) y referido → sí. */}
          {p.discountPercent > 0 && pricing.discountExpires && (
            <p className="text-[10px] text-slate-400">
              Después del periodo con descuento: {mxn(Math.round(p.subtotal * 1.16))}{per}
            </p>
          )}
        </div>

        {/* Hint de prorrateo: solo verticales con sucursales (Flow/crm no las tiene). */}
        {interval === 'yearly' && pricing.branchesApply && (
          <p className="mt-2 text-[10px] leading-snug text-slate-400">
            Si agregas una sucursal a mitad del año, se cobra el restante del año
            prorrateado en tu siguiente factura.
          </p>
        )}

        <button
          onClick={() => subscribe(interval)}
          disabled={busy !== null}
          className="mt-3 w-full rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
        >
          {busy === interval ? 'Abriendo Stripe…' : `Elegir ${title.toLowerCase()}`}
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 px-4" onClick={onClose}>
      <div
        className="card-soft max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
              Activa tu plan
            </p>
            <h2 className="mt-1 font-serif text-xl tracking-tight text-slate-900">
              Elige cómo quieres pagar
            </h2>
          </div>
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-600">✕</button>
        </div>

        {error && (
          <p className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>
        )}
        {!pricing && !error && (
          <p className="mt-4 text-sm text-slate-500">Cargando tu plan…</p>
        )}

        {pricing && (
          <>
            {/* V85 — opt-in de factura ANTES de elegir plan. V217: solo el titular. */}
            {!isOwner ? (
              <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-500">
                La factura (CFDI) de la suscripción la configura el titular de la cuenta
                {user?.ownerName ? ` (${user.ownerName})` : ''} en Configuración → Datos fiscales.
              </p>
            ) : (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={wantsCfdi}
                  onChange={(e) => { setWantsCfdi(e.target.checked); setFiscalError(null); }}
                  className="h-4 w-4 rounded border-slate-300 accent-slate-900"
                />
                <span className="text-sm font-medium text-slate-800">
                  Quiero factura (CFDI) de mi suscripción
                </span>
              </label>
              {wantsCfdi ? (
                <div className="mt-3">
                  {hadData && (
                    <p className="mb-2 text-[11px] text-slate-400">
                      Usaremos estos datos; edítalos si cambiaron.
                    </p>
                  )}
                  <FiscalDataFields form={form} onChange={setForm} catalogs={catalogs} compact />
                  <p className="mt-2 text-[11px] text-slate-400">
                    Captúralos exactamente como aparecen en tu Constancia de Situación Fiscal.
                    Tu factura llega por correo (XML + PDF) con cada pago.
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-slate-400">
                  Sin factura. Puedes activarla después en Configuración → Datos fiscales.
                </p>
              )}
              {fiscalError && (
                <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{fiscalError}</p>
              )}
            </div>
            )}

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              {card('monthly', 'Mensual')}
              {card('yearly', 'Anual', '1 mes gratis')}
            </div>
            <p className="mt-4 text-center text-[11px] text-slate-400">
              El pago se procesa en la página segura de Stripe; nunca tocamos los datos de tu tarjeta.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
